import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, FileArchive, User } from 'lucide-react';
import { AmlChecksArchive, NegativeArchive } from '../report/ArchiveLists';
import { toast } from 'sonner';
import { api, eur, fmtDateTime, downloadFile, errMsg } from '../api';
import { PageHeader, Card, Empty, RatingBadge, SectionTitle, Spinner } from '../ui';

export default function ReportsPage() {
  const [reports, setReports] = useState(null);
  const [docs, setDocs] = useState([]);
  const [people, setPeople] = useState([]);

  useEffect(() => {
    api.get('/cs/reports').then((r) => setReports(r.data)).catch(() => setReports([]));
    api.get('/cs/my-documents').then((r) => setDocs(r.data)).catch(() => {});
    api.get('/cs/people/reports').then((r) => setPeople(r.data)).catch(() => {});
  }, []);

  if (!reports) return <Spinner />;

  return (
    <div>
      <PageHeader kicker="Archivio" title="Report e documenti" />
      <Card className="mb-8">
        {reports.length === 0 ? (
          <Empty icon={FileText} title="Nessun report disponibile" text="I report restano disponibili per 7 giorni dalla richiesta, poi vengono cancellati. Resta il riepilogo contabile nei movimenti del plafond." />
        ) : (
          <div className="overflow-x-auto">
            <p className="px-6 pt-5 text-xs text-[#8A8F97]" data-testid="reports-retention-note">I report restano disponibili per 7 giorni dalla richiesta, poi vengono cancellati. Il monitoraggio dell'azienda resta attivo finché non lo disattivi. <Link to="/portal/guida" className="text-[#D4AF37] hover:underline">Guida</Link></p>
            <table className="w-full text-sm" data-testid="reports-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#5A5F67] border-b border-[#1F242C]">
                <th className="px-6 py-4 font-normal">Rating</th><th className="px-6 py-4 font-normal">Azienda</th><th className="px-6 py-4 font-normal">Template</th><th className="px-6 py-4 font-normal">Data</th><th className="px-6 py-4 font-normal">Disponibile fino al</th><th className="px-6 py-4 font-normal text-right">Costo</th>
              </tr></thead>
              <tbody className="divide-y divide-[#1F242C]">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-6 py-4"><RatingBadge value={r.rating} /></td>
                    <td className="px-6 py-4"><Link to={`/portal/report/${r.id}`} className="text-[#F2F2F2] hover:text-[#D4AF37]" data-testid={`reports-open-${r.id}`}>{r.company_name}</Link><p className="text-xs font-mono text-[#5A5F67]">{r.company_id}</p></td>
                    <td className="px-6 py-4 capitalize text-[#A3A3A3]">{r.template}</td>
                    <td className="px-6 py-4 text-[#A3A3A3] font-mono text-xs">{fmtDateTime(r.created_at)}</td>
                    <td className="px-6 py-4 text-[#A3A3A3] font-mono text-xs" data-testid={`reports-expires-${r.id}`}>{fmtDateTime(r.expires_at)}</td>
                    <td className="px-6 py-4 text-right font-mono">{eur(r.price_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-6 mb-8" data-testid="people-reports-list">
        <SectionTitle>Report esponenti</SectionTitle>
        {people.length === 0 ? <p className="text-sm text-[#5A5F67]">Nessun report esponente. Puoi acquistarli da Ricerca persone.</p> : (
          <div className="divide-y divide-[#1F242C]">
            {people.map((d) => (
              <div key={d.id} className="py-3 flex items-center gap-4">
                <User size={18} className="text-[#D4AF37]" />
                <div className="flex-1 min-w-0">
                  {d.purged_at ? <p className="text-sm truncate text-[#8A8F97]">{d.name}</p> : <Link to={`/portal/report/persona/${d.id}`} className="text-sm text-[#F2F2F2] hover:text-[#D4AF37]" data-testid={`people-report-open-${d.id}`}>{d.name}</Link>}
                  <p className="text-xs text-[#5A5F67] font-mono">{d.people_id?.replace(/^IT-/, '')} · {fmtDateTime(d.created_at)} · {eur(d.price_cents)}{d.purged_at ? ' · scaduto' : ''}</p>
                </div>
                {!d.purged_at && <button onClick={() => downloadFile(`/cs/people/reports/${d.id}/pdf`, `BOND_esponente_${d.people_id}.pdf`).catch((e) => toast.error(errMsg(e)))} className="text-[#D4AF37] hover:text-white" data-testid={`people-report-pdf-${d.id}`}><Download size={16} /></button>}
              </div>
            ))}
          </div>
        )}
      </Card>

      <AmlChecksArchive />
      <NegativeArchive />

      <Card className="p-6">
        <SectionTitle>Visure e bilanci acquistati</SectionTitle>
        {docs.length === 0 ? <p className="text-sm text-[#5A5F67]">Nessun documento. Puoi acquistarli dalla scheda "Documenti ufficiali" di un report.</p> : (
          <div className="divide-y divide-[#1F242C]">
            {docs.map((d) => (
              <div key={d.id} className="py-3 flex items-center gap-4">
                <FileArchive size={18} className="text-[#D4AF37]" />
                <div className="flex-1 min-w-0"><p className="text-sm truncate">{d.label || d.kind} · {d.company_name}</p><p className="text-xs text-[#5A5F67] font-mono">{fmtDateTime(d.created_at)} · {eur(d.price_cents)}</p></div>
                <button onClick={() => downloadFile(`/cs/my-documents/${d.id}/file`, d.filename).catch((e) => toast.error(errMsg(e)))} className="text-[#D4AF37] hover:text-white" data-testid={`doc-download-${d.id}`}><Download size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
